import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Box,
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Avatar,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Divider,
  Badge,
  LinearProgress
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  VolumeUp as VolumeUpIcon,
  Assessment as AssessmentIcon,
  Psychology as PsychologyIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  Star as StarIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Campaign as CampaignIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useSearchParams } from 'react-router-dom';
import { 
  getCallsForReview, 
  getCallForAnalysis,
  submitQAEvaluation,
  generateAIEvaluation,
  getCallAudioURL 
} from '../services/qaService';
import predictionService from '../services/predictionService';

const QA_CRITERIA = {
  communication: { name: 'Communication Skills', maxScore: 20 },
  knowledge: { name: 'Product/Service Knowledge', maxScore: 20 },
  process: { name: 'Process Adherence', maxScore: 25 },
  customerService: { name: 'Customer Service', maxScore: 20 },
  compliance: { name: 'Compliance & Legal', maxScore: 15 }
};

const CallAnalysis = () => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [calls, setCalls] = useState([]);
  const [selectedCall, setSelectedCall] = useState(null);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [audioRef, setAudioRef] = useState(null);
  const [evaluationMode, setEvaluationMode] = useState(false);
  const [evaluation, setEvaluation] = useState({
    scores: {},
    comments: '',
    overallRating: ''
  });
  const [aiEvaluating, setAiEvaluating] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [predicting, setPredicting] = useState(false);
  const [prediction, setPrediction] = useState(null);
  const [filters, setFilters] = useState({
    page: 1,
    limit: 20,
    status: 'pending,processing,completed,analyzed,transcribed,submitted',  // Include all statuses to show calls throughout their lifecycle
    reviewed: 'all'  // Show all calls for analysis (both reviewed and unreviewed)
  });
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    fetchCalls();
  }, [filters]);

  // Debug useEffect to track selectedCall changes
  useEffect(() => {
    console.log('=== SELECTED CALL CHANGED ===');
    console.log('New selectedCall:', selectedCall);
    console.log('Selected call type:', selectedCall?.type);
    console.log('Selected call ID:', selectedCall?.id);
  }, [selectedCall]);

  // Handle automatic call selection from URL parameters
  useEffect(() => {
    const callIdParam = searchParams.get('callId');
    if (callIdParam && calls.length > 0 && !selectedCall) {
      console.log('Auto-selecting call from URL parameter:', callIdParam);
      // Find the call in the list
      const targetCall = calls.find(call => 
        call.id == callIdParam || 
        call.unique_id === callIdParam ||
        call.id === `call_${callIdParam}` // For regular calls
      );
      
      if (targetCall) {
        console.log('Found target call for auto-selection:', targetCall);
        handleCallSelect(targetCall);
      } else {
        console.log('Call not found in current list, may need to fetch calls with different filters');
      }
    }
  }, [calls, searchParams, selectedCall]);

  const fetchCalls = async () => {
    try {
      setLoading(true);
      const response = await getCallsForReview(filters);
      console.log('=== FETCH CALLS DEBUG ===');
      console.log('Filters used:', filters);
      console.log('API Response:', response);
      console.log('Response data:', response.data);
      console.log('Total items:', response.data?.length);
      
      // Process the response data
      if (response.data && response.data.length > 0) {
        // Log each item type
        response.data?.forEach((item, index) => {
          console.log(`Item ${index}:`, {
            id: item.id,
            type: item.type,
            phoneNumber: item.phoneNumber || item.phone || item.lead_data?.phone_number,
            hasLeadData: !!item.lead_data
          });
        });
      }
      
      setCalls(response.data || []);
      setTotalCount(response.count || 0);
    } catch (error) {
      console.error('Error fetching calls:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCallSelect = async (call) => {
    try {
      console.log('=== CALL SELECTION DEBUG ===');
      console.log('Clicked item:', call);
      console.log('Item type:', call.type);
      console.log('Item ID:', call.id);
      console.log('Current selectedCall ID:', selectedCall?.id);
      
      if (call.type === 'lead') {
        // For lead-only analysis, we already have the data
        console.log('Processing lead selection...');
        const leadData = {
          ...call,
          hasRecording: false,
          audioUrl: null,
          type: 'lead' // Ensure type is preserved
        };
        
        console.log('Setting selectedCall to:', leadData);
        setSelectedCall(leadData);
        console.log('Lead selection completed');
      } else {
        // For regular calls, fetch detailed analysis data
        const response = await getCallForAnalysis(call.id);
        const callData = response.data.call;
        
        // Ensure agent data structure is consistent
        if (callData && !callData.agent && callData.Agent) {
          callData.agent = callData.Agent;
        }
        
        setSelectedCall(callData);
        console.log('Selected call data:', callData); // Debug log
        
        // Log prediction data specifically
        if (callData.prediction) {
          console.log('Call has prediction data:', callData.prediction);
        } else {
          console.log('No prediction data found for call');
        }
      }
      
      // Reset evaluation state
      setEvaluationMode(false);
      setEvaluation({
        scores: {},
        comments: '',
        overallRating: ''
      });
      
      // Reset prediction state - let the backend data take precedence
      setPrediction(null);
      
      console.log('=== SELECTION COMPLETED ===');
      console.log('Final selectedCall will be:', call.type === 'lead' ? 'LEAD DATA' : 'CALL DATA');
    } catch (error) {
      console.error('Error fetching call details:', error);
      console.error('Error details:', error.response || error);
      // Don't clear selectedCall on error to prevent blank state
    }
  };

  const toggleAudio = () => {
    if (!audioRef || !selectedCall?.id) return;

    if (audioPlaying) {
      audioRef.pause();
      setAudioPlaying(false);
    } else {
      audioRef.play();
      setAudioPlaying(true);
    }
  };

  const handleEvaluationStart = () => {
    setEvaluationMode(true);
    
    // Pre-populate with existing scores if available
    const qaData = selectedCall?.qa_evaluation || selectedCall?.qa_score;
    if (qaData) {
      const existingScores = {};
      const detailedScores = selectedCall?.qa_evaluation?.metrics?.detailed_scores || selectedCall?.qa_score?.detailed_scores;
      
      if (detailedScores) {
        Object.keys(QA_CRITERIA).forEach(category => {
          const detailedScore = detailedScores[category];
          if (detailedScore) {
            existingScores[category] = detailedScore.score;
          }
        });
      }
      
      setEvaluation({
        scores: existingScores,
        comments: selectedCall?.qa_evaluation?.notes || selectedCall?.qa_score?.comments || '',
        overallRating: selectedCall?.qa_evaluation?.metrics?.overall_rating || selectedCall?.qa_score?.overall_rating || ''
      });
    }
  };

  const handleScoreChange = (category, score) => {
    setEvaluation(prev => ({
      ...prev,
      scores: {
        ...prev.scores,
        [category]: parseInt(score)
      }
    }));
  };

  const handleSubmitEvaluation = async () => {
    try {
      setSubmitting(true);
      
      await submitQAEvaluation(selectedCall.id, {
        scores: evaluation.scores,
        comments: evaluation.comments,
        overallRating: evaluation.overallRating
      });

      // First exit evaluation mode
      setEvaluationMode(false);

      // Refresh the call details with a small delay to ensure backend is updated
      setTimeout(async () => {
        try {
          if (selectedCall.type === 'lead') {
            // For leads, just refresh the calls list
            await fetchCalls();
          } else {
            // For regular calls, fetch fresh call data
            const response = await getCallForAnalysis(selectedCall.id);
            const freshCallData = response.data.call;
            
            // Ensure agent data structure is consistent
            if (freshCallData && !freshCallData.agent && freshCallData.Agent) {
              freshCallData.agent = freshCallData.Agent;
            }
            
            setSelectedCall(freshCallData);
            console.log('Call data refreshed after evaluation:', freshCallData);
            
            // Refresh calls list
            await fetchCalls();
          }
        } catch (refreshError) {
          console.error('Error refreshing after evaluation:', refreshError);
          // Even if refresh fails, keep the evaluation mode off
        }
      }, 1000); // Increased delay to ensure backend processing is complete
      
    } catch (error) {
      console.error('Error submitting evaluation:', error);
      alert('Failed to submit evaluation. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAIEvaluation = async () => {
    try {
      console.log('AI Evaluation started for call:', selectedCall.id);
      setAiEvaluating(true);
      
      const result = await generateAIEvaluation(selectedCall.id);
      console.log('AI Evaluation result:', result);
      
      // Show immediate feedback
      alert('AI evaluation completed successfully! Refreshing data...');
      
      // Refresh the call details with error handling and delay
      setTimeout(async () => {
        try {
          if (selectedCall.type === 'lead') {
            // For leads, just refresh the calls list
            await fetchCalls();
          } else {
            // For regular calls, fetch fresh call data
            const response = await getCallForAnalysis(selectedCall.id);
            const freshCallData = response.data.call;
            
            // Ensure agent data structure is consistent
            if (freshCallData && !freshCallData.agent && freshCallData.Agent) {
              freshCallData.agent = freshCallData.Agent;
            }
            
            setSelectedCall(freshCallData);
            console.log('Call data refreshed after AI evaluation:', freshCallData);
            
            // Refresh calls list
            await fetchCalls();
          }
        } catch (refreshError) {
          console.error('Error refreshing after AI evaluation:', refreshError);
          alert('AI evaluation completed, but failed to refresh data. Please refresh the page manually.');
        }
      }, 500);
      
    } catch (error) {
      console.error('Detailed error generating AI evaluation:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      
      // Show detailed error message
      const errorMessage = error.response?.data?.error || error.message || 'Unknown error occurred';
      alert(`Failed to generate AI evaluation: ${errorMessage}`);
    } finally {
      setAiEvaluating(false);
    }
  };

  const handlePredictOutcome = async () => {
    try {
      setPredicting(true);
      
      const response = await predictionService.generatePrediction(selectedCall.id, 30);
      
      if (response.success) {
        setPrediction(response.data);
        
        // Update the selected call with prediction data
        setSelectedCall(prev => ({
          ...prev,
          prediction: response.data
        }));
        
        alert('Call outcome prediction generated successfully!');
      } else {
        console.error('Prediction failed:', response.error);
        alert(`Failed to generate prediction: ${response.error}`);
      }
    } catch (error) {
      console.error('Error during prediction:', error);
      alert(`Error generating prediction: ${error.message}`);
    } finally {
      setPredicting(false);
    }
  };

  const getScoreBadgeColor = (score, scoreValue = null) => {
    // Handle categorical scores
    if (scoreValue && typeof scoreValue === 'string') {
      switch (scoreValue.toLowerCase()) {
        case 'good':
          return { backgroundColor: '#2196F3', color: 'white' }; // Blue
        case 'average':
          return { backgroundColor: '#FFD700', color: 'black' }; // Yellow
        case 'poor':
          return { backgroundColor: '#9E9E9E', color: 'white' }; // Grey
        case 'rejected':
          return { backgroundColor: '#F44336', color: 'white' }; // Red
        default:
          return { backgroundColor: '#f5f5f5', color: '#666' }; // Default grey
      }
    }
    
    // Handle numerical scores - convert to categorical colors
    if (score >= 80) return { backgroundColor: '#2196F3', color: 'white' }; // Blue (Good)
    if (score >= 60) return { backgroundColor: '#FFD700', color: 'black' }; // Yellow (Average)
    return { backgroundColor: '#F44336', color: 'white' }; // Red (Rejected)
  };

  const calculateTotalScore = () => {
    const scores = Object.values(evaluation.scores);
    if (scores.length === 0) return 0;
    return Math.round(scores.reduce((sum, score) => sum + score, 0));
  };

  const handlePageChange = (event, newPage) => {
    setFilters(prev => ({ ...prev, page: newPage + 1 }));
  };

  const handleRowsPerPageChange = (event) => {
    setFilters(prev => ({ ...prev, page: 1, limit: parseInt(event.target.value) }));
  };

  return (
    <Container maxWidth="xl">
      {/* Header */}
      <Box mb={3}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h4" gutterBottom>
              Call Analysis & QA Review
            </Typography>
            <Typography variant="subtitle1" color="textSecondary">
              Analyze calls and leads with manual and AI-powered quality assurance
            </Typography>
          </Box>
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchCalls}
              disabled={loading}
            >
              Refresh
            </Button>
          </Stack>
        </Stack>
      </Box>

      <Grid container spacing={3}>
        {/* Calls List */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ height: 'calc(100vh - 200px)', display: 'flex', flexDirection: 'column' }}>
            <CardHeader
              title="Calls & Leads for Review"
              action={
                <Badge badgeContent={totalCount} color="primary">
                  <PhoneIcon />
                </Badge>
              }
            />
            <Divider />
            
            {/* Filters */}
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
              <Stack direction="row" spacing={2}>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={filters.reviewed}
                    label="Status"
                    onChange={(e) => setFilters(prev => ({ ...prev, reviewed: e.target.value, page: 1 }))}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="reviewed">Reviewed</MenuItem>
                    <MenuItem value="unreviewed">Unreviewed</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
            </Box>

            {/* Calls Table */}
            <Box sx={{ flex: 1, overflow: 'auto' }}>
              {loading ? (
                <Box display="flex" justifyContent="center" alignItems="center" height="200px">
                  <CircularProgress />
                </Box>
              ) : (
                <TableContainer>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Phone Number</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Agent/Details</TableCell>
                        <TableCell>Score</TableCell>
                        <TableCell>Date</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {calls.map((call) => (
                        <TableRow
                          key={call.id}
                          hover
                          selected={selectedCall?.id === call.id}
                          onClick={() => {
                            console.log('TableRow clicked for:', call.type, call.id);
                            handleCallSelect(call);
                          }}
                          sx={{ 
                            cursor: 'pointer',
                            backgroundColor: selectedCall?.id === call.id ? 'rgba(25, 118, 210, 0.08)' : 'inherit'
                          }}
                        >
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {call.phoneNumber || call.phone || call.lead_data?.phone_number || call.unique_id || call.id}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={call.type === 'lead' ? 'Lead Analysis' : 'Call Recording'}
                              size="small"
                              color={call.type === 'lead' ? 'secondary' : 'primary'}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            {call.type === 'lead' ? (
                              <Box>
                                <Typography variant="body2" fontWeight="medium">
                                  {call.lead_data?.fullname || 'Unknown'}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                  {call.lead_data?.phone_number}
                                </Typography>
                              </Box>
                            ) : (
                              <Stack direction="row" alignItems="center" spacing={1}>
                                <Avatar sx={{ width: 24, height: 24 }}>
                                  <PersonIcon fontSize="small" />
                                </Avatar>
                                <Typography variant="body2">
                                  {call.agent?.name || call.Agent?.name || 'Unknown'}
                                </Typography>
                              </Stack>
                            )}
                          </TableCell>
                          <TableCell>
                            {call.qa_score ? (
                              <Chip
                                label={typeof call.qa_score === 'string' ? call.qa_score : `${call.qa_score.total_score || call.qa_score}%`}
                                sx={getScoreBadgeColor(call.qa_score.total_score || call.qa_score, call.qa_score)}
                                size="small"
                              />
                            ) : (
                              <Chip 
                                label="Not Evaluated" 
                                size="small" 
                                sx={{ backgroundColor: '#f5f5f5', color: '#666' }}
                              />
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {format(new Date(call.created_at || call.createdAt || Date.now()), 'MMM dd, HH:mm')}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>

            {/* Pagination */}
            <TablePagination
              component="div"
              count={totalCount}
              page={filters.page - 1}
              onPageChange={handlePageChange}
              rowsPerPage={filters.limit}
              onRowsPerPageChange={handleRowsPerPageChange}
              rowsPerPageOptions={[10, 20, 50, 100]}
            />
          </Paper>
        </Grid>

        {/* Call Details & Evaluation */}
        <Grid item xs={12} md={6}>
          {selectedCall ? (
            <Paper sx={{ height: 'calc(100vh - 200px)', display: 'flex', flexDirection: 'column' }}>
              <CardHeader
                title={`${selectedCall.type === 'lead' ? 'Lead Analysis' : 'Call Analysis'}: ${selectedCall.unique_id || selectedCall.id}`}
                action={
                  <Stack direction="row" spacing={1}>
                    {!evaluationMode && (
                      <>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<EditIcon />}
                          onClick={handleEvaluationStart}
                        >
                          Evaluate
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<PsychologyIcon />}
                          onClick={handleAIEvaluation}
                          disabled={aiEvaluating}
                        >
                          {aiEvaluating ? 'AI Analyzing...' : 'AI Evaluate'}
                        </Button>
                        {selectedCall.type !== 'lead' && (
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<AssessmentIcon />}
                            onClick={handlePredictOutcome}
                            disabled={predicting || !selectedCall.transcription}
                            color="secondary"
                          >
                            {predicting ? 'Predicting...' : 'Predict Outcome'}
                          </Button>
                        )}
                      </>
                    )}
                  </Stack>
                }
              />
              <Divider />

              {/* Audio Player */}
              {selectedCall.type !== 'lead' && selectedCall.id && (
                <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                  <Typography variant="h6" gutterBottom>
                    Call Recording
                  </Typography>
                  <audio
                    ref={(ref) => setAudioRef(ref)}
                    src={getCallAudioURL(selectedCall.id)}
                    onPlay={() => setAudioPlaying(true)}
                    onPause={() => setAudioPlaying(false)}
                    onEnded={() => setAudioPlaying(false)}
                    onError={(e) => console.error('Audio playback error:', e)}
                    onLoadStart={() => console.log('Audio loading started')}
                    onCanPlay={() => console.log('Audio can play')}
                    controls
                    style={{ width: '100%' }}
                    preload="metadata"
                  />
                  <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                    If audio doesn't load, the recording file may not be available on the server.
                  </Typography>
                </Box>
              )}

              <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                {/* Call/Lead Information */}
                <Card sx={{ mb: 2 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {selectedCall.type === 'lead' ? 'Lead' : 'Call'} Information
                    </Typography>
                    <Grid container spacing={2}>
                      {selectedCall.type === 'lead' ? (
                        <>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="textSecondary">Name</Typography>
                            <Typography variant="body1">{selectedCall.lead_data?.fullname || 'N/A'}</Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="textSecondary">Phone</Typography>
                            <Typography variant="body1">{selectedCall.lead_data?.phone_number || 'N/A'}</Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="textSecondary">Email</Typography>
                            <Typography variant="body1">{selectedCall.lead_data?.email || 'N/A'}</Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="textSecondary">List ID</Typography>
                            <Typography variant="body1">{selectedCall.lead_data?.list_id || 'N/A'}</Typography>
                          </Grid>
                        </>
                      ) : (
                        <>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="textSecondary">Agent</Typography>
                            <Typography variant="body1">
                              {selectedCall.agent?.name || selectedCall.Agent?.name || selectedCall.agent_name || selectedCall.Agent_name || 'Unknown'}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="textSecondary">Duration</Typography>
                            <Typography variant="body1">
                              {selectedCall.duration ? `${Math.floor(selectedCall.duration / 60)}:${String(selectedCall.duration % 60).padStart(2, '0')}` : 'N/A'}
                            </Typography>
                          </Grid>
                        </>
                      )}
                      <Grid item xs={6}>
                        <Typography variant="body2" color="textSecondary">Campaign</Typography>
                        <Typography variant="body1">{selectedCall.campaign || 'N/A'}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="textSecondary">Disposition</Typography>
                        <Chip label={selectedCall.disposition || 'N/A'} size="small" color="primary" />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>

                {/* Lead Analysis Notes (for lead-only analysis) */}
                {selectedCall.type === 'lead' && selectedCall.lead_data?.analysis_notes && (
                  <Card sx={{ mb: 2 }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Analysis Notes
                      </Typography>
                      <Typography variant="body2">
                        {selectedCall.lead_data.analysis_notes}
                      </Typography>
                    </CardContent>
                  </Card>
                )}

                {/* Transcription */}
                {selectedCall.transcription && (
                  <Card sx={{ mb: 2 }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Transcription
                      </Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {selectedCall.transcription}
                      </Typography>
                    </CardContent>
                  </Card>
                )}

                {/* Evaluation Section */}
                {evaluationMode ? (
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        QA Evaluation
                      </Typography>
                      
                      {/* Scoring */}
                      <Grid container spacing={2} sx={{ mb: 3 }}>
                        {Object.entries(QA_CRITERIA).map(([category, criteria]) => (
                          <Grid item xs={12} key={category}>
                            <Box sx={{ mb: 2 }}>
                              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                                <Typography variant="subtitle1">
                                  {criteria.name}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                  Max: {criteria.maxScore} points
                                </Typography>
                              </Stack>
                              <FormControl fullWidth size="small">
                                <InputLabel>Score</InputLabel>
                                <Select
                                  value={evaluation.scores[category] || ''}
                                  label="Score"
                                  onChange={(e) => handleScoreChange(category, e.target.value)}
                                >
                                  {Array.from({ length: criteria.maxScore + 1 }, (_, i) => (
                                    <MenuItem key={i} value={i}>{i}</MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Box>
                          </Grid>
                        ))}
                      </Grid>

                      {/* Total Score Display */}
                      <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                        <Typography variant="h6">
                          Total Score: {calculateTotalScore()}% 
                                                      <Chip 
                              label={calculateTotalScore() >= 90 ? 'Excellent' : calculateTotalScore() >= 80 ? 'Good' : calculateTotalScore() >= 70 ? 'Average' : 'Poor'}
                              sx={{ ...getScoreBadgeColor(calculateTotalScore()), ml: 1 }}
                              size="small"
                          />
                        </Typography>
                      </Box>

                      {/* Comments */}
                      <TextField
                        label="Comments"
                        multiline
                        rows={3}
                        fullWidth
                        value={evaluation.comments}
                        onChange={(e) => setEvaluation(prev => ({ ...prev, comments: e.target.value }))}
                        sx={{ mb: 2 }}
                      />



                      {/* Overall Rating */}
                      <FormControl fullWidth sx={{ mb: 3 }}>
                        <InputLabel>Overall Rating</InputLabel>
                        <Select
                          value={evaluation.overallRating}
                          label="Overall Rating"
                          onChange={(e) => setEvaluation(prev => ({ ...prev, overallRating: e.target.value }))}
                        >
                          <MenuItem value="excellent">Excellent</MenuItem>
                          <MenuItem value="good">Good</MenuItem>
                          <MenuItem value="average">Average</MenuItem>
                          <MenuItem value="poor">Poor</MenuItem>
                        </Select>
                      </FormControl>

                      {/* Action Buttons */}
                      <Stack direction="row" spacing={2}>
                        <Button
                          variant="contained"
                          startIcon={<SaveIcon />}
                          onClick={handleSubmitEvaluation}
                          disabled={submitting || Object.keys(evaluation.scores).length === 0}
                        >
                          {submitting ? 'Saving...' : 'Save Evaluation'}
                        </Button>
                        <Button
                          variant="outlined"
                          onClick={() => setEvaluationMode(false)}
                        >
                          Cancel
                        </Button>
                      </Stack>
                    </CardContent>
                  </Card>
                ) : (selectedCall.qa_score || selectedCall.qa_evaluation) && (
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        QA Results
                      </Typography>
                      
                      {/* Score Summary */}
                      <Box sx={{ mb: 3, p: 2, bgcolor: 'primary.50', borderRadius: 1 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="h5">
                            Overall Score: {(() => {
                              try {
                                const evaluationScore = selectedCall.qa_evaluation?.metrics?.total_score;
                                const simpleScore = selectedCall.qa_score;
                                
                                if (evaluationScore) return `${evaluationScore}%`;
                                if (typeof simpleScore === 'string') return simpleScore;
                                if (typeof simpleScore === 'number') return `${simpleScore}%`;
                                if (simpleScore?.total_score) return `${simpleScore.total_score}%`;
                                return 'N/A';
                              } catch (error) {
                                console.error('Error displaying score:', error);
                                return 'N/A';
                              }
                            })()}
                          </Typography>
                          <Chip
                            label={(() => {
                              try {
                                const evaluationRating = selectedCall.qa_evaluation?.metrics?.overall_rating;
                                const simpleRating = typeof selectedCall.qa_score === 'string' ? selectedCall.qa_score : selectedCall.qa_score?.overall_rating;
                                return evaluationRating || simpleRating || 'N/A';
                              } catch (error) {
                                console.error('Error displaying rating:', error);
                                return 'N/A';
                              }
                            })()}
                            sx={(() => {
                              try {
                                const score = selectedCall.qa_evaluation?.metrics?.total_score ||
                                           (typeof selectedCall.qa_score === 'number' ? selectedCall.qa_score : selectedCall.qa_score?.total_score) || 0;
                                const rating = selectedCall.qa_evaluation?.metrics?.overall_rating || selectedCall.qa_score;
                                return getScoreBadgeColor(score, rating);
                              } catch (error) {
                                console.error('Error getting score color:', error);
                                return { backgroundColor: '#f5f5f5', color: '#666' };
                              }
                            })()}
                          />
                        </Stack>
                      </Box>

                      {/* Detailed Scores */}
                      {(() => {
                        try {
                          const detailedScores = selectedCall.qa_evaluation?.metrics?.detailed_scores || 
                                               (selectedCall.qa_score && typeof selectedCall.qa_score === 'object' && selectedCall.qa_score.detailed_scores);
                          
                          // Debug what we're getting for category scores
                          console.log('=== CALL ANALYSIS CATEGORY SCORES DEBUG ===');
                          console.log('Selected call QA evaluation:', selectedCall.qa_evaluation);
                          console.log('QA evaluation metrics:', selectedCall.qa_evaluation?.metrics);
                          console.log('Detailed scores:', selectedCall.qa_evaluation?.metrics?.detailed_scores);
                          console.log('Categories:', selectedCall.qa_evaluation?.metrics?.categories);
                          console.log('Final detailedScores for display:', detailedScores);
                          
                          // If we have detailed scores, show them
                          if (detailedScores) {
                            return (
                              <Box sx={{ mb: 3 }}>
                                <Typography variant="subtitle1" gutterBottom>
                                  Category Scores
                                </Typography>
                                {Object.keys(QA_CRITERIA).map((category) => {
                                  const scoreData = detailedScores[category];
                                  if (!scoreData) return null; // Skip if no data for this category
                                  
                                  try {
                                    return (
                                      <Box key={category} sx={{ mb: 2 }}>
                                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                                          <Typography variant="body1">
                                            {QA_CRITERIA[category]?.name || category}
                                          </Typography>
                                          <Typography variant="body2">
                                            {scoreData?.score && scoreData?.maxScore ? 
                                              `${scoreData.score}/${scoreData.maxScore} (${Math.round(scoreData.percentage || 0)}%)` :
                                              (scoreData?.category || scoreData?.type || 'N/A')
                                            }
                                          </Typography>
                                        </Stack>
                                        {scoreData?.percentage !== undefined ? (
                                          <LinearProgress
                                            variant="determinate"
                                            value={scoreData.percentage}
                                            sx={{ height: 8, borderRadius: 4 }}
                                          />
                                        ) : (
                                          <Chip
                                            label={scoreData?.category || scoreData?.type || 'N/A'}
                                            size="small"
                                            sx={{
                                              backgroundColor: 
                                                scoreData?.category === 'Good' || scoreData?.type === 'Good' ? '#4caf50' :
                                                scoreData?.category === 'Average' || scoreData?.type === 'Average' ? '#ff9800' :
                                                scoreData?.category === 'Rejected' || scoreData?.type === 'Rejected' ? '#f44336' : '#9e9e9e',
                                              color: 'white'
                                            }}
                                          />
                                        )}
                                        {scoreData?.feedback && (
                                          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                                            {scoreData.feedback}
                                          </Typography>
                                        )}
                                      </Box>
                                    );
                                  } catch (error) {
                                    console.error(`Error rendering category ${category}:`, error);
                                    return (
                                      <Box key={category} sx={{ mb: 2 }}>
                                        <Typography variant="body2" color="error">
                                          Error displaying {category} score
                                        </Typography>
                                      </Box>
                                    );
                                  }
                                })}
                              </Box>
                            );
                          }
                          
                          // If we have a simple QA score but no detailed scores, show explanation
                          if (selectedCall.qa_score && typeof selectedCall.qa_score === 'string' && !detailedScores) {
                            return (
                              <Box sx={{ mb: 3 }}>
                                <Typography variant="subtitle1" gutterBottom>
                                  Evaluation Type
                                </Typography>
                                <Alert severity="info" sx={{ mb: 2 }}>
                                  This is a <strong>Simple QA Evaluation</strong> with an overall categorical rating.
                                  For detailed category breakdowns, use the "Evaluate" button to perform a comprehensive evaluation.
                                </Alert>
                                <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                                  <Typography variant="body2" color="textSecondary">
                                    Simple evaluations provide quick overall assessments using categorical ratings like "Good", "Average", or "Poor".
                                    For detailed scoring across communication, knowledge, process adherence, customer service, and compliance categories,
                                    perform a comprehensive manual evaluation or use AI evaluation.
                                  </Typography>
                                </Box>
                              </Box>
                            );
                          }
                          
                          // If we have both detailed and simple evaluations, show a note about both existing
                          if (detailedScores && selectedCall.qa_score && typeof selectedCall.qa_score === 'string') {
                            return (
                              <Box sx={{ mb: 3 }}>
                                <Alert severity="success" sx={{ mb: 2 }}>
                                  This call has both a <strong>Detailed Evaluation</strong> (shown above) and a <strong>Simple QA rating</strong> of "{selectedCall.qa_score}".
                                  The detailed evaluation provides comprehensive category-based scoring.
                                </Alert>
                              </Box>
                            );
                          }
                          
                          // No evaluation data available
                          return null;
                          
                        } catch (error) {
                          console.error('Error rendering detailed scores:', error);
                          return (
                            <Box sx={{ mb: 3 }}>
                              <Typography variant="body2" color="error">
                                Error displaying detailed scores
                              </Typography>
                            </Box>
                          );
                        }
                      })()}

                                            {/* Comments */}
                      {(() => {
                        try {
                          const comments = selectedCall.qa_evaluation?.notes || 
                                         (selectedCall.qa_score && typeof selectedCall.qa_score === 'object' && selectedCall.qa_score.comments);
                          
                          if (!comments) return null;
                          
                          return (
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="subtitle1" gutterBottom>
                                Comments
                              </Typography>
                              <Typography variant="body2">
                                {comments}
                              </Typography>
                            </Box>
                          );
                        } catch (error) {
                          console.error('Error displaying comments:', error);
                          return null;
                        }
                      })()}

                      {/* Evaluation Info */}
                      <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                        <Typography variant="caption" color="textSecondary">
                          {(() => {
                            try {
                              const reviewer = selectedCall.qa_evaluation?.reviewer?.name || 
                                             (selectedCall.qa_score && typeof selectedCall.qa_score === 'object' ? selectedCall.qa_score.evaluated_by || 'User' : 'User');
                              const evaluationType = selectedCall.qa_evaluation?.metrics?.evaluation_type || 
                                                    (selectedCall.qa_score && typeof selectedCall.qa_score === 'object' ? selectedCall.qa_score.evaluation_type || 'manual' : 'manual');
                              const evaluatedDate = selectedCall.qa_evaluation?.reviewDate || 
                                                   (selectedCall.qa_score && typeof selectedCall.qa_score === 'object' ? selectedCall.qa_score.evaluated_at : null) ||
                                                   selectedCall.created_at || selectedCall.createdAt || Date.now();
                              
                              return `Evaluated by: ${reviewer} | Type: ${evaluationType} | Date: ${format(new Date(evaluatedDate), 'MMM dd, yyyy HH:mm')}`;
                            } catch (error) {
                              console.error('Error displaying evaluation info:', error);
                              return 'Evaluation information unavailable';
                            }
                          })()}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                )}

                {/* Call Outcome Prediction Results */}
                {(selectedCall.prediction || prediction) && (
                  <Card sx={{ mb: 2 }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        🎯 Call Outcome Prediction
                      </Typography>
                      
                      {(() => {
                        const predictionData = selectedCall.prediction || prediction;
                        const riskColor = predictionService.getRiskColor(predictionData.riskLevel);
                        const IconComponent = predictionData.riskLevel === 'high' ? WarningIcon : 
                                            predictionData.riskLevel === 'medium' ? InfoIcon : CheckCircleIcon;
                        
                        return (
                          <Box>
                            {/* Risk Level & Score */}
                            <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                              <Stack direction="row" alignItems="center" spacing={2}>
                                <IconComponent style={{ color: riskColor, fontSize: 32 }} />
                                <Box>
                                  <Typography variant="h5" style={{ color: riskColor }}>
                                    {predictionData.riskLevel.toUpperCase()} RISK
                                  </Typography>
                                  <Typography variant="body1">
                                    Success Probability: {predictionService.formatScore(predictionData.predictionScore)}
                                  </Typography>
                                  <Typography variant="body2" color="textSecondary">
                                    Confidence: {predictionService.formatConfidence(predictionData.confidence)}
                                  </Typography>
                                </Box>
                              </Stack>
                            </Box>

                            {/* Prediction Summary */}
                            <Box sx={{ mb: 3 }}>
                              <Typography variant="subtitle1" gutterBottom>
                                Assessment Summary
                              </Typography>
                              <Typography variant="body2">
                                {predictionService.getPredictionSummary(predictionData)}
                              </Typography>
                            </Box>

                            {/* Key Indicators */}
                            {predictionData.keyIndicators && (
                              <Box sx={{ mb: 3 }}>
                                <Typography variant="subtitle1" gutterBottom>
                                  Key Indicators
                                </Typography>
                                
                                {predictionData.keyIndicators.positiveSignals?.length > 0 && (
                                  <Box sx={{ mb: 2 }}>
                                    <Typography variant="body2" color="success.main" gutterBottom>
                                      ✅ Positive Signals
                                    </Typography>
                                    <Box display="flex" flexWrap="wrap" gap={1}>
                                      {predictionData.keyIndicators.positiveSignals.map((signal, index) => (
                                        <Chip key={index} label={signal} color="success" size="small" />
                                      ))}
                                    </Box>
                                  </Box>
                                )}

                                {predictionData.keyIndicators.negativeSignals?.length > 0 && (
                                  <Box sx={{ mb: 2 }}>
                                    <Typography variant="body2" color="error.main" gutterBottom>
                                      ⚠️ Negative Signals
                                    </Typography>
                                    <Box display="flex" flexWrap="wrap" gap={1}>
                                      {predictionData.keyIndicators.negativeSignals.map((signal, index) => (
                                        <Chip key={index} label={signal} color="error" size="small" />
                                      ))}
                                    </Box>
                                  </Box>
                                )}
                              </Box>
                            )}

                            {/* Recommendations */}
                            <Box sx={{ mb: 3 }}>
                              <Typography variant="subtitle1" gutterBottom>
                                Recommended Actions
                              </Typography>
                              <Box component="ul" sx={{ pl: 2, m: 0 }}>
                                {predictionService.getRecommendations(predictionData).map((rec, index) => (
                                  <Typography component="li" key={index} variant="body2" sx={{ mb: 1 }}>
                                    {rec}
                                  </Typography>
                                ))}
                              </Box>
                            </Box>

                            {/* Sentiment Analysis */}
                            {predictionData.sentimentAnalysis && (
                              <Box sx={{ mb: 3 }}>
                                <Typography variant="subtitle1" gutterBottom>
                                  Sentiment Analysis
                                </Typography>
                                <Grid container spacing={2}>
                                  <Grid item xs={12} sm={4}>
                                    <Typography variant="body2" color="textSecondary">
                                      Customer Sentiment
                                    </Typography>
                                    <Chip 
                                      label={predictionData.sentimentAnalysis.customerSentiment} 
                                      size="small"
                                      color={predictionData.sentimentAnalysis.customerSentiment === 'positive' ? 'success' : 
                                             predictionData.sentimentAnalysis.customerSentiment === 'negative' ? 'error' : 'default'}
                                    />
                                  </Grid>
                                  <Grid item xs={12} sm={4}>
                                    <Typography variant="body2" color="textSecondary">
                                      Agent Tone
                                    </Typography>
                                    <Typography variant="body2">
                                      {predictionData.sentimentAnalysis.agentTone}
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={12} sm={4}>
                                    <Typography variant="body2" color="textSecondary">
                                      Overall Mood
                                    </Typography>
                                    <Typography variant="body2">
                                      {predictionData.sentimentAnalysis.overallMood}
                                    </Typography>
                                  </Grid>
                                </Grid>
                              </Box>
                            )}

                            {/* Prediction Info */}
                            <Box sx={{ pt: 2, borderTop: 1, borderColor: 'divider' }}>
                              <Typography variant="caption" color="textSecondary">
                                Analysis based on first {predictionData.timeframe} seconds | 
                                Generated: {format(new Date(predictionData.createdAt || Date.now()), 'MMM dd, yyyy HH:mm')}
                              </Typography>
                            </Box>
                          </Box>
                        );
                      })()}
                    </CardContent>
                  </Card>
                )}
              </Box>
            </Paper>
          ) : (
            <Paper sx={{ height: 'calc(100vh - 200px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Box textAlign="center">
                <AssessmentIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
                <Typography variant="h6" color="textSecondary">
                  Select a call or lead to begin analysis
                </Typography>
              </Box>
            </Paper>
          )}
        </Grid>
      </Grid>
    </Container>
  );
};

export default CallAnalysis; 